import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Clock, Mail } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Logo from "../components/Logo";
import CatalogueRequest from "../components/CatalogueRequest";
import Seo from "../components/Seo";

const ALBAGLI_COVER = "/exhibition/albagli-cover.jpg";
const AKHUNDOVA_COVER = "/exhibition/akhundova-cover.jpg";
const AKHUNDOVA_PORTRAIT = "/exhibition/akhundova-portrait.png";
const ALBAGLI_PORTRAIT = "/exhibition/albagli-portrait.jpg";

const ARTIST_BIOS = [
  {
    id: "albagli",
    name: "Guilherme Albagli",
    locale: "Ilhéus, Bahia · b. 1954",
    image: ALBAGLI_PORTRAIT,
    paragraphs: [
      "Guilherme Albagli (b. 1954, Ilhéus, Bahia) is a Brazilian painter and muralist whose work is rooted in the sacred, the humanistic and the deeply Bahian. Of Sephardic Jewish and Brazilian-Portuguese descent, he has produced a body of work over six decades that encompasses painting, public murals, scenography, and restoration — including major works for the Cathedral Basilica of Salvador and the State University of Santa Cruz.",
      "His artistic formation was forged outside institutional orthodoxy, through direct mentorships with painters and makers in Ilhéus and Rio de Janeiro, studies in architecture and the humanities, and sustained engagement with sacred imagery, popular tradition, and the discipline of materials. Formative encounters include time spent in the studio of Domenico Lazzarini in Ipanema, and a lasting dialogue with the Swiss painter Marie Louise Schmidt (Emeles).",
      "At the centre of his pictorial work is the human figure — most often presented frontally, with direct gaze and solemn stillness. These figures do not perform for the viewer; they meet them. They are bearers of memory, faith, and what the artist describes as a belief in the possibility of ethical and spiritual renewal for humanity.",
    ],
  },
  {
    id: "akhundova",
    name: "Ulviyya Akhundova",
    locale: "London / Baku",
    image: AKHUNDOVA_PORTRAIT,
    paragraphs: [
      "Ulviyya Akhundova's art is a vivid exploration of the world around us, capturing the ways we perceive and experience emotion. Each painting transforms observation and memory into a poetic reflection of life.",
      "Her work reveals a deep sensitivity to the human condition, inviting contemplation and connection. Nature is a constant source of inspiration, breathing life into her creations, from the warmth of sunrise and the stillness of forests to the vibrant rhythm of the city.",
      "Educated at the University of the Arts London in Fine Art Painting and at Sotheby's Institute of Art in Art Business, Ulviyya combines creative expression with a refined understanding of the contemporary art world.",
    ],
  },
];

const CATALOG = [
  {
    n: "01",
    artist: "Ulviyya Akhundova",
    title: "New York",
    year: "2021",
    medium: "Oil on canvas",
    dimensions: "100 × 70 cm",
    image: "/exhibition/akhundova-new-york-2021.jpg",
    note: "",
  },
  {
    n: "02",
    artist: "Ulviyya Akhundova",
    title: "Night Road",
    year: "2024",
    medium: "Oil on canvas",
    dimensions: "180 × 175 cm",
    image: "/exhibition/akhundova-night-road-2024.jpg",
    note: "",
  },
  {
    n: "03",
    artist: "Ulviyya Akhundova",
    title: "Fujiyama",
    year: "2024",
    medium: "Oil on canvas",
    dimensions: "180 × 150 cm",
    image: "/exhibition/akhundova-fujiyama-2024.jpg",
    note: "",
  },
  {
    n: "04",
    artist: "Ulviyya Akhundova",
    title: "Another Dimension",
    year: "2020",
    medium: "Oil on canvas",
    dimensions: "80 × 60 cm",
    image: "/exhibition/akhundova-another-dimension-2020.jpg",
    note: "",
  },
  {
    n: "05",
    artist: "Guilherme Albagli",
    title: "Garota da Praia",
    titleAlt: "Girl of the beach",
    year: "2025",
    medium: "Oil on canvas",
    dimensions: "81.5 × 70.5 cm",
    image: "/exhibition/albagli-garota-da-praia-2025.jpg",
    note: "Garota da Praia is both a homecoming and a statement of intent. Painted in 2025, the year of the artist's London debut, it places a reclining nude at the centre of a Bahian landscape: a hillside colonial town climbs behind her, its terracotta rooftops and palm trees rising toward a violet sea and open sky. She is unhurried, unguarded, and entirely at ease. The town does not dominate her; she contains it.",
  },
  {
    n: "06",
    artist: "Guilherme Albagli",
    title: "Sol",
    year: "2007",
    medium: "Oil on canvas",
    dimensions: "80 × 91.5 cm",
    image: "/exhibition/albagli-sol-2007.jpg",
    note: "Sol is among the most concentrated expressions of Albagli's lifelong preoccupation with the frontal human figure as a bearer of memory, dignity, and light.",
  },
  {
    n: "07",
    artist: "Guilherme Albagli",
    title: "Silent Green",
    year: "Undated",
    medium: "Oil on paper",
    dimensions: "52.5 × 70 cm",
    image: "/exhibition/albagli-silent-green.jpg",
    note: "Where Sol consecrates the frontal face through Baroque ornament and the devotional weight of oil paint, Silent Green strips that ceremony away. What remains is the irreducible core of Albagli's practice — the human face as bearer of memory and presence — rendered here with a speed and economy that makes its stillness feel all the more considered.",
  },
  {
    n: "08",
    artist: "Ulviyya Akhundova",
    title: "Sunny Wind",
    year: "2024",
    medium: "Oil on canvas",
    dimensions: "100 × 120 cm",
    image: "/exhibition/akhundova-sunny-wind-2024.jpg",
    note: "",
  },
];

const CURATORIAL = [
  "The Shape of Elsewhere brings together Guilherme Albagli and Ulviyya Akhundova in a dialogue between place, memory, and imagination.",
  "Across different generations and geographies, both artists transform the world around them into something deeply internal. Landscapes, cities, architecture, light, and the human figure become more than subjects; they become carriers of emotion, belonging, and cultural memory.",
  "Rooted in Bahia, Albagli's paintings hold the weight of history and spiritual presence. His figures are still, frontal, and quietly powerful, shaped by sacred imagery, Brazilian modernism, and the layered cultural memory of the Global South.",
  "Akhundova approaches place through colour, rhythm, and feeling. Her paintings turn cities, interiors, nature, and imagined views into vivid emotional landscapes. Buildings bend, trees glow, water reflects, and light becomes almost musical.",
  "Together, the two artists ask how elsewhere takes shape: in the body, in memory, in colour, and in the spaces we carry within us. The exhibition becomes a meeting point between the sacred and the dreamlike, the grounded and the imagined, the personal and the universal.",
];

export default function ShapeOfElsewhere() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      data-testid="event-page"
      className="bg-nyg-paper text-nyg-dark min-h-screen"
    >
      <Seo
        title="Shape of Elsewhere — Guilherme Albagli & Ulviyya Akhundova | NYG Projects"
        description="NYG × Tramp London: The Shape of Elsewhere, a dialogue between place, memory and imagination with works by Guilherme Albagli and Ulviyya Akhundova. 23 June 2026, by invitation."
        image="/share/og-shape-of-elsewhere.jpg"
        imageAlt="The Shape of Elsewhere — exhibition invitation, Tramp London, 23 June 2026"
        path="/projects/shape-of-elsewhere"
        type="article"
      />
      <Nav variant="light" />

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <Link
            to="/"
            data-testid="event-back-link"
            className="inline-flex items-center gap-2 label hover:opacity-60 transition-opacity"
          >
            <ArrowLeft size={14} strokeWidth={1} /> Back
          </Link>

          {/* Two invitation posters side-by-side */}
          <div className="grid grid-cols-12 gap-6 md:gap-10 mt-10 md:mt-14 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 md:col-span-6"
            >
              <div className="overflow-hidden">
                <img
                  src="/exhibition/invitation-1.jpg"
                  alt="The Shape of Elsewhere — Invitation"
                  className="w-full h-auto block"
                  data-testid="invitation-1"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 md:col-span-6"
            >
              <div className="overflow-hidden">
                <img
                  src="/exhibition/invitation-2.jpg"
                  alt="The Shape of Elsewhere — Invitation"
                  className="w-full h-auto block"
                  data-testid="invitation-2"
                />
              </div>
            </motion.div>
          </div>

          <div className="mt-14 md:mt-20">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="label block"
            >
              NYG × Tramp London — N° 01
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              data-testid="event-title"
              className="font-heading text-[52px] sm:text-7xl md:text-8xl lg:text-[140px] leading-[0.9] tracking-[-0.02em] mt-6"
            >
              Shape of
              <br />
              <span className="italic font-light">Elsewhere</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="font-body font-light italic text-lg md:text-2xl text-nyg-dark/70 mt-8"
            >
              Guilherme Albagli &amp; Ulviyya Akhundova
            </motion.p>
          </div>
        </div>
      </section>

      {/* DETAILS STRIP */}
      <section className="bg-nyg-dark text-nyg-cream py-16 md:py-20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-6">
          {[
            { icon: <Calendar size={18} strokeWidth={1} />, label: "Date", value: "23 June 2026" },
            { icon: <Clock size={18} strokeWidth={1} />, label: "Time", value: "6:00 – 8:00 PM GMT" },
            { icon: <MapPin size={18} strokeWidth={1} />, label: "Venue", value: "Tramp London\n40 Jermyn Street, SW1Y 6DN" },
            { icon: <Mail size={18} strokeWidth={1} />, label: "RSVP", value: "By invitation only\nRSVP by 16 June" },
          ].map((item, i) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
              data-testid={`event-detail-${item.label.toLowerCase()}`}
              className="flex flex-col gap-3"
            >
              <div className="flex items-center gap-3 text-nyg-cream/80">
                {item.icon}
                <span className="label !text-nyg-cream/70">{item.label}</span>
              </div>
              <div className="font-heading text-2xl md:text-[28px] leading-tight whitespace-pre-line">
                {item.value}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CURATORIAL STATEMENT */}
      <section className="py-24 md:py-40 bg-nyg-paper">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-6 md:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1 }}
            className="col-span-12 md:col-span-4"
          >
            <span className="label">Curatorial Note</span>
            <h2 className="font-heading text-4xl md:text-5xl leading-[0.95] tracking-tight mt-6">
              A dialogue
              <br />
              between
              <br />
              <span className="italic font-light">place &amp; memory.</span>
            </h2>
          </motion.div>

          <div className="col-span-12 md:col-span-7 md:col-start-6 space-y-7 md:space-y-9">
            {CURATORIAL.map((p, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.9, delay: i * 0.05 }}
                className="font-body font-light text-lg md:text-xl leading-relaxed text-nyg-dark/85"
                data-testid={`curatorial-paragraph-${i}`}
              >
                {p}
              </motion.p>
            ))}
          </div>
        </div>
      </section>

      {/* ARTISTS */}
      <section className="bg-nyg-cream py-24 md:py-40 border-t border-nyg-dark/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-baseline justify-between mb-16 md:mb-24">
            <span className="label">The Artists</span>
            <span className="label hidden sm:inline">Two Voices</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20">
            {ARTIST_BIOS.map((a, i) => (
              <motion.article
                key={a.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`event-artist-${i}`}
              >
                <div className="overflow-hidden aspect-[3/4] bg-nyg-dark mb-8 flex items-center justify-center max-w-[340px]">
                  <img
                    src={a.image}
                    alt={a.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="label !text-nyg-dark/60">{a.locale}</span>
                <h3 className="font-heading italic font-light text-4xl md:text-5xl text-nyg-dark mt-4">
                  {a.name}
                </h3>
                <div className="h-px w-12 bg-nyg-dark my-7" />
                <div className="space-y-5">
                  {a.paragraphs.map((p, k) => (
                    <p
                      key={k}
                      className="font-body font-light text-base md:text-lg leading-relaxed text-nyg-dark/85"
                    >
                      {p}
                    </p>
                  ))}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CATALOG */}
      <section
        id="catalog"
        data-testid="catalog-section"
        className="bg-nyg-paper py-24 md:py-40 border-t border-nyg-dark/10"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1 }}
            className="flex items-baseline justify-between mb-16 md:mb-24"
          >
            <div>
              <span className="label">Art Catalogue</span>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-6">
                Selected
                <br />
                <span className="italic font-light">works.</span>
              </h2>
            </div>
            <span className="label hidden sm:inline">{CATALOG.length} works</span>
          </motion.div>

          <div className="space-y-24 md:space-y-32">
            {CATALOG.map((w, i) => {
              const reverse = i % 2 === 1;
              return (
                <motion.article
                  key={w.n}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`catalog-item-${i}`}
                  className={`grid grid-cols-12 gap-6 md:gap-12 items-center ${
                    reverse ? "md:[direction:rtl]" : ""
                  }`}
                >
                  <div className="col-span-12 md:col-span-5 md:[direction:ltr]">
                    <div className="overflow-hidden">
                      <img
                        src={w.image}
                        alt={`${w.title}, ${w.year}`}
                        className="w-full h-auto block"
                      />
                    </div>
                  </div>

                  <div className="col-span-12 md:col-span-6 md:col-start-7 md:[direction:ltr]">
                    <span className="label !text-nyg-dark/50">N° {w.n}</span>
                    <h3 className="font-heading text-4xl md:text-5xl lg:text-[56px] leading-[0.95] tracking-tight mt-5">
                      {w.title}
                      <span className="italic font-light text-nyg-dark/60">
                        , {w.year}
                      </span>
                    </h3>
                    {w.titleAlt && (
                      <p className="font-body italic text-base text-nyg-dark/50 mt-2">
                        {w.titleAlt}
                      </p>
                    )}
                    <div className="h-px w-12 bg-nyg-dark my-7" />
                    <dl className="space-y-3">
                      <div className="flex gap-6">
                        <dt className="label w-28 shrink-0">Artist</dt>
                        <dd className="font-body font-light text-base md:text-lg">
                          {w.artist}
                        </dd>
                      </div>
                      <div className="flex gap-6">
                        <dt className="label w-28 shrink-0">Medium</dt>
                        <dd className="font-body font-light text-base md:text-lg">
                          {w.medium}
                        </dd>
                      </div>
                      <div className="flex gap-6">
                        <dt className="label w-28 shrink-0">Dimensions</dt>
                        <dd className="font-body font-light text-base md:text-lg">
                          {w.dimensions}
                        </dd>
                      </div>
                    </dl>
                    {w.note && (
                      <p className="font-body font-light italic text-base text-nyg-dark/75 leading-relaxed mt-8 max-w-md">
                        {w.note}
                      </p>
                    )}
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* REQUEST CATALOGUE */}
      <CatalogueRequest />

      {/* CURATED BY */}
      <section className="bg-nyg-dark text-nyg-cream py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-6 md:gap-10 items-center">
          <div className="col-span-12 md:col-span-4 flex justify-center md:justify-start">
            <Logo size={70} tone="cream" />
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6">
            <span className="label !text-nyg-cream/60">Curated by</span>
            <p className="font-heading text-3xl md:text-4xl leading-snug mt-5">
              Nicolette Matakova,
              <br />
              Yasemin Solu <span className="italic font-light">&amp;</span> Gizem Ataman
            </p>
            <p className="font-body font-light text-base text-nyg-cream/70 mt-8 max-w-lg">
              Presented in collaboration with Tramp London. An intimate evening
              by invitation — written enquiries welcome at{" "}
              <a
                href="mailto:info@nygprojects.com"
                className="underline underline-offset-4 hover:text-nyg-cream"
              >
                info@nygprojects.com
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
